import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
  Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '~/services/api';
import IconButton from '~/components/IconButton';
import { copyStringToClipboard } from '~/util/copyToClipboard';

const STATUS_STYLE = {
  delivered: { bg: '#10b981', label: 'Entregue' },
  pending: { bg: '#f59e0b', label: 'Aguardando retry' },
  in_flight: { bg: '#6b7280', label: 'Em processamento' },
  retried: { bg: '#6366f1', label: 'Sobrescrita por retry' },
  failed_permanently: { bg: '#ef4444', label: 'Falhou definitivamente' },
};

const httpColor = code => {
  if (!code) return '#9ca3af';
  if (code >= 200 && code < 300) return '#10b981';
  if (code >= 300 && code < 400) return '#3b82f6';
  if (code >= 400 && code < 500) return '#f59e0b';
  return '#ef4444';
};

const formatJson = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    // Tenta parse — se for JSON, repretty. Se não, retorna como é.
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const formatDate = iso => {
  if (!iso) return null;
  try {
    return format(parseISO(iso), "dd/MMM/yy - HH'h'mm'min'", {
      locale: ptBR,
    });
  } catch (e) {
    return null;
  }
};

/**
 * Modal de detalhe de WebhookDispatch.
 *
 * Mostra payload completo, response body, contexto da PartnerApplication
 * e botão de Replay (re-tentativa manual).
 */
export default function DispatchDetailModal({
  show,
  setShow,
  dispatch: dispatchFromList,
  onReplayed,
}) {
  const [replaying, setReplaying] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Quando o modal abre, busca o detalhe completo do dispatch.
  // O `index` do backend usa WebhookDispatchListItemResource (versão reduzida),
  // que omite payload e webhook_url. O `show` retorna WebhookDispatchResource
  // completo.
  useEffect(() => {
    if (!show || !dispatchFromList?.id) {
      setDetail(null);
      return () => {};
    }

    let cancelled = false;
    setLoadingDetail(true);
    api
      .get(`administrator/webhook-dispatches/${dispatchFromList.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDetail(data?.data || data);
      })
      .catch(err => {
        if (cancelled) return;
        // Mantém o que veio da lista como fallback
        toast.error(
          err?.response?.data?.message ||
            'Não foi possível carregar o detalhe completo.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, dispatchFromList?.id]);

  // Usa detalhe completo se disponível; caso contrário, dados da lista
  const dispatch = detail || dispatchFromList;

  const handleClose = () => {
    if (replaying) return;
    setShow(false);
  };

  const handleReplay = async () => {
    if (!dispatchFromList?.id) return;
    setReplaying(true);
    try {
      const { data } = await api.post(
        `administrator/webhook-dispatches/${dispatchFromList.id}/replay`
      );
      const newId = data?.data?.id;
      const shortId = newId ? `${newId.substring(0, 8)}...` : '';
      toast.success(
        `Replay criado! Novo dispatch ${shortId} aparecerá no topo da lista.`
      );
      if (onReplayed) onReplayed();
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Não foi possível executar o replay.');
      }
    } finally {
      setReplaying(false);
    }
  };

  if (!dispatchFromList) return null;

  const status = STATUS_STYLE[dispatch.status] || STATUS_STYLE.in_flight;
  const payloadPretty = formatJson(dispatch.payload);
  const responseBodyPretty = formatJson(dispatch.response_body);

  return (
    <Modal isOpen={show} toggle={handleClose} size="xl" scrollable>
      <ModalHeader toggle={handleClose}>
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <span
            style={{
              fontFamily:
                '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
              fontSize: '15px',
              fontWeight: 600,
            }}
          >
            {dispatch.event_type}
          </span>
          <Badge
            style={{
              backgroundColor: status.bg,
              color: '#fff',
              fontSize: '11px',
              padding: '4px 10px',
            }}
          >
            {status.label}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Bloco de metadata */}
        <Row className="mb-3">
          <Col lg={6}>
            <div className="text-muted font-size-12 mb-1">Aplicação</div>
            <div className="font-weight-600">
              {dispatch?.partner_application?.name || '—'}
              {dispatch?.partner_application && (
                <Badge
                  className="ml-2"
                  style={{
                    backgroundColor:
                      dispatch.partner_application.environment === 'live'
                        ? '#2544b1'
                        : '#6b7280',
                    color: '#fff',
                    fontSize: '10px',
                    padding: '2px 8px',
                  }}
                >
                  {dispatch.partner_application.environment === 'live'
                    ? 'PRODUÇÃO'
                    : 'TESTE'}
                </Badge>
              )}
            </div>

            {dispatch.event_id && (
              <>
                <div className="text-muted font-size-12 mb-1 mt-3">
                  Event ID
                </div>
                <div className="d-flex align-items-center">
                  <code
                    style={{
                      fontFamily: '"JetBrains Mono", Menlo, monospace',
                      fontSize: '12px',
                      backgroundColor: '#f3f4f6',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dispatch.event_id}
                  </code>
                  <IconButton
                    icon="las la-copy"
                    containerClass="ml-2"
                    fontSize={18}
                    color="info"
                    click={() =>
                      copyStringToClipboard(
                        dispatch.event_id,
                        'Event ID copiado!'
                      )
                    }
                    tip="Copiar"
                  />
                </div>
              </>
            )}

            <div className="text-muted font-size-12 mb-1 mt-3">URL destino</div>
            <div className="d-flex align-items-center">
              <code
                style={{
                  fontFamily: '"JetBrains Mono", Menlo, monospace',
                  fontSize: '12px',
                  backgroundColor: '#f3f4f6',
                  padding: '3px 6px',
                  borderRadius: '4px',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {dispatch.webhook_url || '—'}
              </code>
              {dispatch.webhook_url && (
                <IconButton
                  icon="las la-copy"
                  containerClass="ml-2"
                  fontSize={18}
                  color="info"
                  click={() =>
                    copyStringToClipboard(dispatch.webhook_url, 'URL copiada!')
                  }
                  tip="Copiar"
                />
              )}
            </div>
          </Col>

          <Col lg={6}>
            <Row>
              <Col xs={6}>
                <div className="text-muted font-size-12 mb-1">Tentativas</div>
                <div className="font-weight-600 font-size-18">
                  {dispatch.attempt_count || 0}
                </div>
              </Col>
              <Col xs={6}>
                <div className="text-muted font-size-12 mb-1">
                  HTTP da última resposta
                </div>
                <div
                  className="font-weight-600 font-size-18"
                  style={{
                    fontFamily: '"JetBrains Mono", Menlo, monospace',
                    color: httpColor(dispatch.response_status),
                  }}
                >
                  {dispatch.response_status || '—'}
                </div>
              </Col>
            </Row>

            <div className="text-muted font-size-12 mb-1 mt-3">Criado em</div>
            <div>{formatDate(dispatch.created_at) || '—'}</div>

            {formatDate(dispatch.delivered_at) && (
              <>
                <div className="text-muted font-size-12 mb-1 mt-2">
                  Entregue em
                </div>
                <div>{formatDate(dispatch.delivered_at)}</div>
              </>
            )}

            {formatDate(dispatch.next_retry_at) && (
              <>
                <div className="text-muted font-size-12 mb-1 mt-2">
                  Próximo retry
                </div>
                <div>{formatDate(dispatch.next_retry_at)}</div>
              </>
            )}
          </Col>
        </Row>

        {dispatch.failed_permanently && dispatch.failure_reason && (
          <Alert color="danger">
            <strong>Falha permanente:</strong> {dispatch.failure_reason}
          </Alert>
        )}

        {/* Payload */}
        <div className="mt-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <strong className="font-size-14">Payload enviado</strong>
            {payloadPretty && (
              <IconButton
                icon="las la-copy"
                fontSize={18}
                color="info"
                click={() =>
                  copyStringToClipboard(payloadPretty, 'Payload copiado!')
                }
                tip="Copiar payload"
                id="copy-payload"
              />
            )}
          </div>
          <pre
            style={{
              backgroundColor: '#1f2937',
              color: '#e5e7eb',
              padding: '12px',
              borderRadius: '6px',
              maxHeight: '320px',
              overflowY: 'auto',
              fontSize: '12px',
              fontFamily:
                '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
              margin: 0,
            }}
          >
            {loadingDetail && !payloadPretty
              ? 'Carregando payload...'
              : payloadPretty || '—'}
          </pre>
        </div>

        {/* Response body */}
        {responseBodyPretty && (
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong className="font-size-14">Resposta do parceiro</strong>
              <IconButton
                icon="las la-copy"
                fontSize={18}
                color="info"
                click={() =>
                  copyStringToClipboard(responseBodyPretty, 'Response copiada!')
                }
                tip="Copiar resposta"
                id="copy-response"
              />
            </div>
            <pre
              style={{
                backgroundColor: '#1f2937',
                color: '#e5e7eb',
                padding: '12px',
                borderRadius: '6px',
                maxHeight: '240px',
                overflowY: 'auto',
                fontSize: '12px',
                fontFamily:
                  '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                margin: 0,
              }}
            >
              {responseBodyPretty}
            </pre>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={replaying}>
          Fechar
        </Button>
        <Button
          color="info"
          className="d-inline-flex align-items-center"
          onClick={handleReplay}
          disabled={replaying}
        >
          <i className="las la-redo mr-1 font-size-16" />
          {replaying ? 'Replicando...' : 'Replay'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
